import { useEffect, useState, useCallback } from 'react';
import { Loading } from './PokladnaUi';
import { Plus, Save, Check, X } from 'lucide-react';
import { fmtKc } from './PokladnaPrehledTab';

const ACCENT = '#3B82F6';

export default function PokladnaNastaveniTab({ cardStyle, isDark, refreshKey, isCeo }) {
    const [fleky, setFleky] = useState([]);
    const [hlavniFlek, setHlavniFlek] = useState(null); // bistro_hlavni — v tabulce se needituje, kartu ale ano
    const [hlavniKarta, setHlavniKarta] = useState('');
    const [konfig, setKonfig] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState({}); // { [flekId]: {nazev, pocatecni, poradi} }
    const [konfigEdits, setKonfigEdits] = useState({}); // { [klic]: hodnota }
    const [adding, setAdding] = useState(false);
    const [novy, setNovy] = useState({ nazev: '', pocatecni_castka_kc: 2000, karta_posledni4: '' });

    const token = document.querySelector('meta[name="csrf-token"]')?.content;

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([
            fetch('/api/finance/pokladna/cis-fleky', { credentials: 'same-origin' }).then(r => r.json()),
            fetch('/api/finance/pokladna/konfigurace', { credentials: 'same-origin' }).then(r => r.json()),
        ]).then(([f, k]) => {
            // Hlavní pokladna má jinou logiku (trvale otevřená, drží ji aktuální
            // manažer směny) — nekonfiguruje se ze stejného UI jako fleky rozvozců.
            // Kartu (pro návrh párování kartových dokladů z bistra) ale nastavit jde.
            const hf = (f || []).find(x => x.typ === 'bistro_hlavni') || null;
            setHlavniFlek(hf);
            setHlavniKarta(hf?.karta_posledni4 || '');
            setFleky((f || []).filter(x => x.typ !== 'bistro_hlavni'));
            setKonfig(k);
        }).catch(console.error).finally(() => setLoading(false));
    }, []);
    useEffect(() => { load(); }, [load, refreshKey]);

    const saveHlavniKarta = async () => {
        if (!hlavniFlek) return;
        try {
            const r = await fetch(`/api/finance/pokladna/cis-fleky/${hlavniFlek.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
                credentials: 'same-origin',
                body: JSON.stringify({ karta_posledni4: (hlavniKarta || '').trim() || null }),
            });
            if (!r.ok) throw new Error(await r.text());
            load();
        } catch (e) { alert('Uložení karty selhalo: ' + e.message); }
    };

    const startEdit = (flek) => setEditing(e => ({ ...e, [flek.id]: { nazev: flek.nazev, pocatecni_castka_kc: flek.pocatecni_castka_kc, poradi: flek.poradi, karta_posledni4: flek.karta_posledni4 || '' } }));
    const cancelEdit = (id) => setEditing(e => { const { [id]: _, ...rest } = e; return rest; });

    const saveEdit = async (id) => {
        const data = { ...editing[id], karta_posledni4: (editing[id].karta_posledni4 || '').trim() || null };
        try {
            const r = await fetch(`/api/finance/pokladna/cis-fleky/${id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
                credentials: 'same-origin',
                body: JSON.stringify(data),
            });
            if (!r.ok) throw new Error(await r.text());
            cancelEdit(id);
            load();
        } catch (e) { alert('Uložení selhalo: ' + e.message); }
    };

    const toggleAktivni = async (flek) => {
        try {
            const r = await fetch(`/api/finance/pokladna/cis-fleky/${flek.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
                credentials: 'same-origin',
                body: JSON.stringify({ aktivni: !flek.aktivni }),
            });
            if (!r.ok) throw new Error(await r.text());
            load();
        } catch (e) { alert('Změna stavu selhala: ' + e.message); }
    };

    const addNew = async () => {
        if (!novy.nazev || novy.nazev.trim().length < 2) {
            alert('Zadej název.');
            return;
        }
        try {
            const payload = { ...novy, karta_posledni4: (novy.karta_posledni4 || '').trim() || null };
            const r = await fetch('/api/finance/pokladna/cis-fleky', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
                credentials: 'same-origin',
                body: JSON.stringify(payload),
            });
            if (!r.ok) throw new Error(await r.text());
            setAdding(false);
            setNovy({ nazev: '', pocatecni_castka_kc: 2000, karta_posledni4: '' });
            load();
        } catch (e) { alert('Vytvoření selhalo: ' + e.message); }
    };

    const saveKonfig = async (klic) => {
        const hodnota = konfigEdits[klic];
        if (hodnota === undefined) return;
        try {
            const r = await fetch(`/api/finance/pokladna/konfigurace/${klic}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
                credentials: 'same-origin',
                body: JSON.stringify({ hodnota: String(hodnota) }),
            });
            if (!r.ok) throw new Error(await r.text());
            setKonfigEdits(e => { const { [klic]: _, ...rest } = e; return rest; });
            load();
        } catch (e) { alert('Uložení konfigurace selhalo: ' + e.message); }
    };

    return (
        <div className="space-y-5">
            {/* Karta hlavní pokladny — hlavní flek se v tabulce needituje, kartu ale nastavit lze */}
            {hlavniFlek && (
                <div className="rounded-2xl p-3 flex flex-wrap items-center gap-3" style={cardStyle}>
                    <div className="flex-1 min-w-[200px]">
                        <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Hlavní pokladna (bistro) — karta</div>
                        <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                            Poslední 4 čísla karty bistra pro návrh párování kartových dokladů s bankou.
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <input inputMode="numeric" maxLength={4} value={hlavniKarta}
                            onChange={e => setHlavniKarta(e.target.value.replace(/\D/g, '').slice(0, 4))}
                            placeholder="1234"
                            className="rounded px-2 py-1 text-xs" style={{ width: 72, background: 'var(--surface-base)', color: 'var(--text-primary)', border: '1px solid var(--border-default)' }} />
                        <button onClick={saveHlavniKarta}
                            disabled={(hlavniKarta || '') === (hlavniFlek.karta_posledni4 || '')}
                            className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium text-white"
                            style={{ background: '#10b981', opacity: (hlavniKarta || '') === (hlavniFlek.karta_posledni4 || '') ? 0.5 : 1 }}>
                            <Save size={11} /> Uložit
                        </button>
                    </div>
                </div>
            )}

            {/* Fleky tabulka */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Fleky (peněženky)</h3>
                    {!adding && (
                        <button onClick={() => setAdding(true)}
                            className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-medium text-white" style={{ background: ACCENT }}>
                            <Plus size={12} /> Přidat flek rozvozce
                        </button>
                    )}
                </div>

                <div className="rounded-2xl overflow-hidden" style={cardStyle}>
                    {loading ? <Loading /> : (
                        <table className="w-full text-xs">
                            <thead><tr className="text-[10.5px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)', textAlign: 'left', borderBottom: '1px solid var(--border-default)' }}>
                                <th className="py-2 px-3" style={{ width: 50 }}>Pořadí</th>
                                <th>Název</th>
                                <th>Typ</th>
                                <th className="text-right">Počáteční částka</th>
                                <th>Karta</th>
                                <th>Stav</th>
                                <th></th>
                            </tr></thead>
                            <tbody>
                                {adding && (
                                    <tr style={{ borderTop: '1px solid var(--border-default)', background: ACCENT + '10' }}>
                                        <td className="py-1.5 px-3" style={{ color: 'var(--text-tertiary)' }}>auto</td>
                                        <td>
                                            <input value={novy.nazev} onChange={e => setNovy(n => ({ ...n, nazev: e.target.value }))}
                                                placeholder="např. Flek rozvozce #4" autoFocus
                                                className="rounded px-2 py-1 text-xs w-full"
                                                style={{ background: 'var(--surface-base)', color: 'var(--text-primary)', border: '1px solid var(--border-default)' }} />
                                        </td>
                                        <td><span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'var(--surface-base)' }}>rozvoz</span></td>
                                        <td className="text-right">
                                            <input type="number" step="0.01" min="0" value={novy.pocatecni_castka_kc} onChange={e => setNovy(n => ({ ...n, pocatecni_castka_kc: Number(e.target.value) }))}
                                                className="rounded px-2 py-1 text-xs text-right" style={{ width: 100, background: 'var(--surface-base)', color: 'var(--text-primary)', border: '1px solid var(--border-default)' }} />
                                        </td>
                                        <td>
                                            <input inputMode="numeric" maxLength={4} value={novy.karta_posledni4}
                                                onChange={e => setNovy(n => ({ ...n, karta_posledni4: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                                                placeholder="1234"
                                                className="rounded px-2 py-1 text-xs" style={{ width: 64, background: 'var(--surface-base)', color: 'var(--text-primary)', border: '1px solid var(--border-default)' }} />
                                        </td>
                                        <td>—</td>
                                        <td className="flex gap-1 py-1.5">
                                            <button onClick={addNew} className="p-1 rounded" style={{ background: '#10b981', color: 'white' }}><Check size={12} /></button>
                                            <button onClick={() => { setAdding(false); setNovy({ nazev: '', pocatecni_castka_kc: 2000, karta_posledni4: '' }); }} className="p-1 rounded" style={{ background: 'var(--surface-base)', color: 'var(--text-secondary)' }}><X size={12} /></button>
                                        </td>
                                    </tr>
                                )}
                                {fleky.length === 0 && !adding && (
                                    <tr><td colSpan={7} className="py-4 text-center" style={{ color: 'var(--text-tertiary)' }}>Žádné fleky.</td></tr>
                                )}
                                {fleky.map(f => {
                                    const ed = editing[f.id];
                                    const isHlavni = f.typ === 'bistro_hlavni';
                                    return (
                                        <tr key={f.id} style={{ borderTop: '1px solid var(--border-default)', color: 'var(--text-primary)', opacity: f.aktivni ? 1 : 0.5 }}>
                                            <td className="py-1.5 px-3">
                                                {ed ? (
                                                    <input type="number" min="0" value={ed.poradi ?? 0} onChange={e => setEditing(es => ({ ...es, [f.id]: { ...ed, poradi: Number(e.target.value) } }))}
                                                        className="rounded px-1 py-0.5 text-xs" style={{ width: 50, background: 'var(--surface-base)', color: 'var(--text-primary)', border: '1px solid var(--border-default)' }} />
                                                ) : f.poradi}
                                            </td>
                                            <td>
                                                {ed ? (
                                                    <input value={ed.nazev} onChange={e => setEditing(es => ({ ...es, [f.id]: { ...ed, nazev: e.target.value } }))}
                                                        className="rounded px-2 py-1 text-xs w-full" style={{ background: 'var(--surface-base)', color: 'var(--text-primary)', border: '1px solid var(--border-default)' }} />
                                                ) : (
                                                    <strong>{f.nazev}</strong>
                                                )}
                                            </td>
                                            <td><span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'var(--surface-base)' }}>{isHlavni ? 'hlavní' : 'rozvoz'}</span></td>
                                            <td className="text-right">
                                                {ed ? (
                                                    <input type="number" step="0.01" min="0" value={ed.pocatecni_castka_kc} onChange={e => setEditing(es => ({ ...es, [f.id]: { ...ed, pocatecni_castka_kc: Number(e.target.value) } }))}
                                                        className="rounded px-2 py-1 text-xs text-right" style={{ width: 100, background: 'var(--surface-base)', color: 'var(--text-primary)', border: '1px solid var(--border-default)' }} />
                                                ) : fmtKc(f.pocatecni_castka_kc)}
                                            </td>
                                            <td>
                                                {ed ? (
                                                    <input inputMode="numeric" maxLength={4} value={ed.karta_posledni4}
                                                        onChange={e => setEditing(es => ({ ...es, [f.id]: { ...ed, karta_posledni4: e.target.value.replace(/\D/g, '').slice(0, 4) } }))}
                                                        placeholder="1234"
                                                        className="rounded px-2 py-1 text-xs" style={{ width: 64, background: 'var(--surface-base)', color: 'var(--text-primary)', border: '1px solid var(--border-default)' }} />
                                                ) : (f.karta_posledni4
                                                    ? <span className="font-mono" style={{ color: 'var(--text-secondary)' }}>••{f.karta_posledni4}</span>
                                                    : <span style={{ color: 'var(--text-tertiary)' }}>—</span>)}
                                            </td>
                                            <td>
                                                <button onClick={() => toggleAktivni(f)}
                                                    className="text-xs px-2 py-0.5 rounded font-medium"
                                                    style={{ background: f.aktivni ? '#10b98122' : '#6b728022', color: f.aktivni ? '#10b981' : '#6b7280' }}>
                                                    {f.aktivni ? 'Aktivní' : 'Neaktivní'}
                                                </button>
                                            </td>
                                            <td className="flex gap-1 py-1.5">
                                                {ed ? (
                                                    <>
                                                        <button onClick={() => saveEdit(f.id)} className="p-1 rounded" style={{ background: '#10b981', color: 'white' }}><Check size={12} /></button>
                                                        <button onClick={() => cancelEdit(f.id)} className="p-1 rounded" style={{ background: 'var(--surface-base)', color: 'var(--text-secondary)' }}><X size={12} /></button>
                                                    </>
                                                ) : (
                                                    <button onClick={() => startEdit(f)} className="text-xs px-2 py-1 rounded" style={{ background: 'var(--surface-base)', color: 'var(--text-primary)', border: '1px solid var(--border-default)' }}>Upravit</button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
                <p className="text-xs mt-2" style={{ color: 'var(--text-tertiary)' }}>
                    Pouze fleky rozvozců — hlavní pokladna se nekonfiguruje (systémový singleton, drží ji aktuální manažer směny).
                    Mazání není podporováno — místo toho přepni na „Neaktivní" (zachová historii smen).
                    <br /><strong>Karta</strong> = poslední 4 čísla platební karty přidělené fleku; slouží k návrhu párování kartových dokladů s bankovním výpisem.
                </p>
            </div>

            {/* Konfigurace */}
            <div>
                <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                    Konfigurace {!isCeo && <span className="text-xs font-normal" style={{ color: 'var(--text-tertiary)' }}>(úpravy jen CEO)</span>}
                </h3>
                <div className="rounded-2xl overflow-hidden" style={cardStyle}>
                    {loading ? <Loading /> : (
                        <table className="w-full text-xs">
                            <thead><tr className="text-[10.5px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)', textAlign: 'left', borderBottom: '1px solid var(--border-default)' }}>
                                <th className="py-2 px-3" style={{ width: 220 }}>Klíč</th>
                                <th style={{ width: 140 }}>Hodnota</th>
                                <th>Popis</th>
                                <th></th>
                            </tr></thead>
                            <tbody>
                                {konfig.map(k => {
                                    const novaHodnota = konfigEdits[k.klic] ?? k.hodnota;
                                    const zmeneno = konfigEdits[k.klic] !== undefined && String(konfigEdits[k.klic]) !== String(k.hodnota);
                                    return (
                                        <tr key={k.klic} style={{ borderTop: '1px solid var(--border-default)', color: 'var(--text-primary)' }}>
                                            <td className="py-1.5 px-3 font-mono" style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{k.klic}</td>
                                            <td>
                                                <input
                                                    type={k.typ === 'integer' || k.typ === 'decimal' ? 'number' : 'text'}
                                                    step={k.typ === 'decimal' ? '0.01' : '1'}
                                                    value={novaHodnota}
                                                    disabled={!isCeo}
                                                    onChange={e => setKonfigEdits(es => ({ ...es, [k.klic]: e.target.value }))}
                                                    className="rounded px-2 py-1 text-xs"
                                                    style={{ width: 130, background: 'var(--surface-base)', color: 'var(--text-primary)', border: '1px solid var(--border-default)' }} />
                                            </td>
                                            <td style={{ color: 'var(--text-tertiary)' }}>{k.popis}</td>
                                            <td className="py-1.5 pr-3">
                                                {zmeneno && isCeo && (
                                                    <button onClick={() => saveKonfig(k.klic)}
                                                        className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-white"
                                                        style={{ background: '#10b981' }}>
                                                        <Save size={11} /> Uložit
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}
